import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DraggableLocation } from 'react-beautiful-dnd';

import { Form, FormFiled } from '../../models';
import { uuid } from '../../utils';
import { ITEMS } from './utils';
import { formsApiSlice } from '../../services';

const initialState: Form = {
  name: '',
  features: [],
  fields: [],
};

const __reorderFields = ({ fields }: Form) => {
  return fields.map((field, index) => ({
    ...field,
    orderNumber: index + 1,
  }));
};

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    updateTemplateName: (
      state,
      { payload }: PayloadAction<{ name: string }>
    ) => {
      state.name = payload.name;
    },
    updateTemplateFeature: (
      state,
      { payload }: PayloadAction<{ featureId: number; isEnabled: boolean }>
    ) => {
      const featureIndex = state.features.findIndex(
        f => f.featureTypeId === payload.featureId
      );

      if (featureIndex !== -1) {
        state.features[featureIndex].isEnabled = payload.isEnabled;
      } else {
        // Add the feature if it doesn't exist
        state.features.push({
          featureTypeId: payload.featureId,
          isEnabled: payload.isEnabled,
        });
      }
    },
    updateTemplateField: (
      state,
      { payload }: PayloadAction<{ field: Omit<FormFiled, 'orderNumber'> }>
    ) => {
      const fieldIndex = state.fields.findIndex(f => f.id === payload.field.id);
      if (fieldIndex !== -1)
        state.fields[fieldIndex] = {
          ...state.fields[fieldIndex],
          ...payload.field,
        };
    },
    // a little function to help us with reordering the result
    reorder: (
      state,
      {
        payload: { startIndex, endIndex },
      }: PayloadAction<{ startIndex: number; endIndex: number }>
    ) => {
      const [removed] = state.fields.splice(startIndex, 1);
      state.fields.splice(endIndex, 0, removed);

      state.fields = __reorderFields(state);
    },
    /**
     * Moves an item from one list to another list.
     */
    copy: (
      state,
      {
        payload: { droppableSource, droppableDestination },
      }: PayloadAction<{
        droppableSource: DraggableLocation;
        droppableDestination: DraggableLocation;
      }>
    ) => {
      const item = ITEMS[droppableSource.index];

      state.fields.splice(droppableDestination.index, 0, {
        ...item,
        id: uuid(),
      });

      state.fields = __reorderFields(state);
    },
    removeTemplateField: (
      state,
      { payload }: PayloadAction<{ id: string }>
    ) => {
      const itemIndex = state.fields.findIndex(
        item => String(item.id) === payload.id
      );
      if (itemIndex !== -1) {
        state.fields.splice(itemIndex, 1);

        state.fields = __reorderFields(state);
      }
    },
    duplicateTemplateField: (
      state,
      { payload }: PayloadAction<{ field: FormFiled }>
    ) => {
      const itemIndex = state.fields.findIndex(
        item => item.id === payload.field.id
      );

      const duplicatedField = {
        ...payload.field,
        id: uuid(),
        orderNumber: payload.field.orderNumber + 1,
      };

      if (itemIndex !== -1)
        state.fields.splice(itemIndex + 1, 0, duplicatedField);

      state.fields = __reorderFields(state);
    },
    resetFormTemplate: state => {
      Object.keys(state).forEach(key => {
        state[key] = initialState[key];
      });
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      formsApiSlice.endpoints.getAllFormFeatures.matchFulfilled,
      (state, { payload }) => {
        // Initialize features with all available features set to true by default
        if (!state.features.length) {
          state.features = payload.data.map(({ id }) => ({
            featureTypeId: id,
            isEnabled: true,
          }));
        }
      }
    );

    builder.addMatcher(
      formsApiSlice.endpoints.getFormTemplateById.matchFulfilled,
      (state, { payload }) => {
        Object.keys(state).forEach(key => {
          state[key] = payload.data[key];
        });
      }
    );
  },
});

export const {
  reorder,
  copy,
  removeTemplateField,
  duplicateTemplateField,
  updateTemplateName,
  updateTemplateFeature,
  updateTemplateField,
  resetFormTemplate,
} = formSlice.actions;
export const formReducer = formSlice.reducer;
